import { Box, Button, Flex, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { ChevronDown, X } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { t } from '@lingui/core/macro';
import upperFirst from 'lodash-es/upperFirst.js';

import { UnitGenerationRunStatus } from '@duke-hq/svc-api-db/client';
import { iconSize } from '@duke-hq/ui/constants/scalable';

function formatFilterTitle(label: string) {
  return upperFirst(label.replace(/_/g, ' ').toLowerCase());
}

type RunFilterValues = {
  status?: UnitGenerationRunStatus;
};

type RunFiltersProps = {
  filters: RunFilterValues;
  onChange: (newFilters: RunFilterValues) => void;
};

export function RunFilters({ filters, onChange }: RunFiltersProps) {
  const { status } = filters;

  const handleFilterChange = useCallback(
    (key: keyof RunFilterValues, value: RunFilterValues[keyof RunFilterValues]) => {
      onChange({ ...filters, [key]: value });
    },
    [filters, onChange],
  );

  const statusOptions = useMemo(() => {
    return Object.values(UnitGenerationRunStatus).map((status) => ({
      id: status,
      value: status,
      label: formatFilterTitle(status),
    }));
  }, []);

  const statusLabel = status != null ? formatFilterTitle(status) : t`All Statuses`;

  return (
    <Flex gap={2}>
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          px={2}
          colorScheme="gray"
          rightIcon={
            status != null ? (
              <Box
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilterChange('status', undefined);
                }}
              >
                <X size={iconSize.md} />
              </Box>
            ) : (
              <ChevronDown size={iconSize.md} />
            )
          }
        >
          {statusLabel}
        </MenuButton>
        <MenuList>
          {statusOptions.map((option) => (
            <MenuItem key={option.id} onClick={() => handleFilterChange('status', option.value)}>
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Flex>
  );
}
